import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
} from '@mui/material';
import { Topic, TopicFormData } from '../../services/topics';
import { Planner } from '../../services/planners';

interface TopicFormProps {
  open: boolean;
  topic: Topic | null;
  planners: Planner[];
  onSave: (data: TopicFormData) => void;
  onClose: () => void;
  onDelete?: (topic: Topic, cascade: boolean) => void;
}

const EMOJI_OPTIONS = [
  '📋', '💒', '🎂', '💐', '🎵', '📸', '🍽️', '👗', '💍', '🚗',
  '🎤', '🏨', '✈️', '💇', '🎁', '📨', '🕯️', '🎉', '🪩', '💄',
  '👠', '🥂', '🎶', '🌸', '🧁', '🫧', '✨', '💝', '🎊', '🪻',
  '💌', '🎀', '🌹', '🎪', '🍰', '🎻', '💎', '🕊️', '👰', '🤵',
  '🫶', '💃', '🪄', '🌺', '🏵️',
];

const initialForm: TopicFormData = {
  name: '',
  emoji: null,
  status: 'not_started',
  deadline: '',
  assigned_planner_id: '',
  notes: '',
};

const TopicForm: React.FC<TopicFormProps> = ({ open, topic, planners, onSave, onClose, onDelete }) => {
  const [form, setForm] = useState<TopicFormData>(initialForm);
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (topic) {
      setForm({
        name: topic.name,
        emoji: topic.emoji || null,
        status: topic.status,
        deadline: topic.deadline ? topic.deadline.slice(0, 10) : '',
        assigned_planner_id: topic.assigned_planner_id || '',
        notes: topic.notes || '',
      });
    } else {
      setForm(initialForm);
    }
  }, [topic, open]);

  const handleChange = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const data: TopicFormData = {
      ...form,
      assigned_planner_id: form.assigned_planner_id || null,
      deadline: form.deadline || null,
    };
    onSave(data);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{topic ? 'Edit Topic' : 'New Topic'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box
                  onClick={(e) => setEmojiAnchor(e.currentTarget)}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: 24,
                    flexShrink: 0,
                    '&:hover': { borderColor: 'grey.500' },
                  }}
                >
                  {form.emoji || '🏷️'}
                </Box>
                <TextField
                  label="Name"
                  fullWidth
                  required
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                />
              </Box>
              <Popover
                open={!!emojiAnchor}
                anchorEl={emojiAnchor}
                onClose={() => setEmojiAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1.5, maxWidth: 280 }}>
                  {EMOJI_OPTIONS.map((emoji) => (
                    <Box
                      key={emoji}
                      onClick={() => { handleChange('emoji', emoji); setEmojiAnchor(null); }}
                      sx={{
                        width: 36,
                        height: 36,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1,
                        cursor: 'pointer',
                        fontSize: 20,
                        '&:hover': { bgcolor: 'grey.100' },
                        ...(form.emoji === emoji && { bgcolor: 'primary.light' }),
                      }}
                    >
                      {emoji}
                    </Box>
                  ))}
                </Box>
              </Popover>
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.status}
                  label="Status"
                  onChange={(e) => handleChange('status', e.target.value)}
                >
                  <MenuItem value="not_started">Not Started</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="covered">Covered</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6}>
              <TextField
                label="Deadline"
                type="date"
                fullWidth
                value={form.deadline}
                onChange={(e) => handleChange('deadline', e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Assigned Planner</InputLabel>
                <Select
                  value={form.assigned_planner_id}
                  label="Assigned Planner"
                  onChange={(e) => handleChange('assigned_planner_id', e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {planners.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6} />
            <Grid size={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={2}
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: topic && onDelete ? 'space-between' : 'flex-end' }}>
          {topic && onDelete && (
            <Button onClick={() => setDeleteDialogOpen(true)} sx={{ color: 'error.main' }}>Delete</Button>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} sx={{ color: 'grey.600' }}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!form.name?.trim()}>
              {topic ? 'Save' : 'Create'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Custom delete dialog with cascade options */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Topic</DialogTitle>
        <DialogContent>
          How would you like to handle the tasks under "{topic?.name}"?
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="error"
            onClick={() => {
              setDeleteDialogOpen(false);
              onClose();
              if (topic && onDelete) onDelete(topic, true);
            }}
          >
            Delete topic and its tasks
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            onClick={() => {
              setDeleteDialogOpen(false);
              onClose();
              if (topic && onDelete) onDelete(topic, false);
            }}
          >
            Delete topic, keep tasks
          </Button>
          <Button
            fullWidth
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: 'grey.600' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TopicForm;
